/**
 * Small indicator chip for the header/toolbar showing offline status.
 *
 * `compact` (mobile app bar) renders the chip icon-only — the label stays in
 * the DOM visually hidden for screen readers and the tooltip keeps the full
 * text — so the chip can't crowd the user menu out of a narrow app bar.
 */
import { CloudOff, RefreshCw, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import { useOfflineStatus } from '../OfflineStatusContext';
import { useUserPreferences } from '../../context';

interface OfflineIndicatorProps {
  compact?: boolean;
}

export function OfflineIndicator({ compact = false }: OfflineIndicatorProps) {
  const { t } = useTranslation('common');
  const { hasConnectivity, syncStatus, lastSyncedAt } = useOfflineStatus();
  const { formatDateTime } = useUserPreferences();

  // Don't show anything when online and idle
  if (hasConnectivity && syncStatus === 'idle') {
    return null;
  }

  const labelClass = cn(compact && 'sr-only');

  // Offline state
  if (!hasConnectivity) {
    const lastSyncText = lastSyncedAt
      ? t('offline.lastSynced', 'Last synced: {{time}}', {
          time: formatDateTime(lastSyncedAt, 'short'),
        })
      : t('offline.neverSynced', 'Never synced');

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="warning" className="ml-2 shrink-0 cursor-default gap-1">
            <CloudOff className="h-3 w-3" />
            <span className={labelClass}>{t('offline.offline', 'Offline')}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('offline.workingOffline', 'Working offline. Changes will sync when connected.')}</p>
          <p className="text-xs text-muted-foreground">{lastSyncText}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Syncing state
  if (syncStatus === 'syncing') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className="ml-2 shrink-0 cursor-default gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span className={labelClass}>{t('offline.syncing', 'Syncing')}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('offline.syncing', 'Syncing changes...')}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Error state
  if (syncStatus === 'error') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="destructive" className="ml-2 shrink-0 cursor-default gap-1">
            <RefreshCw className="h-3 w-3" />
            <span className={labelClass}>{t('offline.syncFailed', 'Sync failed')}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('offline.syncError', 'Sync failed. Will retry automatically.')}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return null;
}

export default OfflineIndicator;
